/**
 * `/` — the filter.
 *
 * A single centred column. Not a hero, not a dashboard -- a form whose job
 * is to narrow 13 companies down to the ones you meant (§10.1).
 */
import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'

import type { FiltersResponse } from '@server/sql/filters.ts'
import { api, keys, qs } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Button, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { ErrorState } from '~/components/ui/empty.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'
import { ChipRow, CompanyList, Group } from '~/features/filters/controls.tsx'
import {
  CASCADE, selectionToSearch, useDebounced, useSelection,
  type Dimension, type Selection,
} from '~/features/filters/state.ts'
import { ApiError } from '~/lib/api.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { useDocumentTitle } from '~/lib/title.ts'

export function ExploreScreen() {
  const { t, tn, locale, v } = useT()
  useDocumentTitle(t('nav.explore'))
  const superadmin = useSuperadmin()
  const [, navigate] = useLocation()
  const { selection, set, toggle, setDimension, clear } = useSelection()

  // One faceted query per settled selection, rather than a chain of
  // dependent ones. A chain has ordering bugs; a single query cannot (§10.1).
  const debounced = useDebounced(selection, 150)
  const query = selectionToSearch(debounced)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: keys.filters(query),
    queryFn: () => api<FiltersResponse>(`/api/filters${query}`),
    // Keep the previous facets on screen while the next ones load, so the
    // whole form does not blink on every keystroke.
    placeholderData: (prev) => prev,
  })

  // --- auto-select a level that has exactly one option -----------------
  // Clicking through a choice that is not a choice is not a choice.
  useEffect(() => {
    if (!data) return
    const next = { ...selection }
    let changed = false
    if (data.sectors.length === 1 && selection.sector.length === 0) {
      next.sector = [data.sectors[0]!.value]; changed = true
    }
    if (data.industries.length === 1 && selection.industry.length === 0) {
      next.industry = [data.industries[0]!.value]; changed = true
    }
    if (changed) set(next)
  }, [data, selection, set])

  // --- prune selections a higher level invalidated ---------------------
  // Silently dropping a selection is how a filter loses someone's trust, so
  // the removal is announced inline and stays until the next change.
  const [pruned, setPruned] = useState<{ dimension: Dimension; n: number } | null>(null)
  const lastPrune = useRef<string>('')

  useEffect(() => {
    if (!data) return
    const alive: Partial<Record<Dimension, Set<string>>> = {
      sector: new Set(data.sectors.map((s) => s.value)),
      industry: new Set(data.industries.map((s) => s.value)),
      symbol: new Set(data.companies.map((c) => c.symbol)),
    }
    for (const d of CASCADE) {
      const keep = selection[d].filter((x) => alive[d]!.has(x))
      if (keep.length !== selection[d].length) {
        const sig = `${d}:${selection[d].join(',')}`
        if (lastPrune.current === sig) return
        lastPrune.current = sig
        setPruned({ dimension: d, n: selection[d].length - keep.length })
        set({ ...selection, [d]: keep })
        return
      }
    }
  }, [data, selection, set])

  const dimensionLabel = (d: Dimension): string =>
    ({
      sector: t('filter.sector'), industry: t('filter.industry'),
      symbol: t('filter.companies'), docType: t('filter.docType'),
      period: t('filter.period'), year: t('filter.year'),
      locale: t('filter.locale'), quality: t('filter.quality'),
    })[d]

  const explore = () => navigate(`/results${selectionToSearch(selection)}`)

  if (error) {
    const e = error as ApiError
    return (
      <Column>
        <ErrorState
          title={t('common.error')}
          detail={e.message}
          requestId={e.requestId}
          onRetry={() => void refetch()}
          retryLabel={t('action.retry')}
        />
      </Column>
    )
  }

  if (!data) return <ExploreSkeleton />

  const { corpus, matched } = data
  const narrowed = matched.documents < corpus.documents
  const nothing = matched.documents === 0

  return (
    <Column>
      {/* --- corpus header ------------------------------------------- */}
      <div className="border-b border-[var(--border)] pb-3">
        <h1 className="text-[length:var(--text-display)] font-semibold tracking-tight">
          {t('filter.title')}
        </h1>
        <p className="tnum mt-0.5 text-[length:var(--text-data)] text-[var(--text-muted)]">
          {tn('common.documents', corpus.documents, { n: count(corpus.documents, locale) })}
          <Sep />
          {tn('common.companies', corpus.companies, { n: count(corpus.companies, locale) })}
          {corpus.firstYear && (
            <>
              <Sep />
              {corpus.firstYear}–{corpus.lastYear}
            </>
          )}
        </p>
      </div>

      {/* --- cascade -------------------------------------------------- */}
      <Group
        label={t('filter.sector')}
        muted={data.sectors.length === 1}
        aside={data.sectors.length === 1 ? t('filter.onlyOption') : undefined}
      >
        <ChipRow
          options={data.sectors.map((s) => ({
            value: s.value,
            label: locale === 'ar' && s.labelAr ? <Raw lang="ar">{s.labelAr}</Raw> : s.labelEn,
            documents: s.documents,
            available: s.available,
          }))}
          selected={selection.sector}
          onToggle={(x) => toggle('sector', x)}
        />
      </Group>

      <Group
        label={t('filter.industry')}
        muted={data.industries.length === 1}
        aside={data.industries.length === 1 ? t('filter.onlyOption') : undefined}
      >
        <ChipRow
          options={data.industries.map((s) => ({
            value: s.value,
            label: v('industry', s.value) ?? <Untranslated>{s.value}</Untranslated>,
            documents: s.documents,
            available: s.available,
          }))}
          selected={selection.industry}
          onToggle={(x) => toggle('industry', x)}
        />
      </Group>

      <Group
        label={t('filter.companies')}
        aside={
          pruned?.dimension === 'symbol' ? (
            <span className="text-[var(--warn)]">
              {tn('filter.pruned', pruned.n, { n: pruned.n, level: t('filter.industry') })}
            </span>
          ) : undefined
        }
      >
        <CompanyList
          companies={data.companies}
          selected={selection.symbol}
          onToggle={(x) => toggle('symbol', x)}
          onSetAll={(xs) => setDimension('symbol', xs)}
        />
      </Group>

      {/* --- the flat facets ------------------------------------------ */}
      <div className="grid gap-x-6 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Group label={t('filter.period')}>
          <ChipRow
            options={data.periods.map((p) => ({
              value: p.value,
              label: v('period', p.value) ?? <Untranslated>{p.value}</Untranslated>,
              documents: p.documents,
              available: p.available,
            }))}
            selected={selection.period}
            onToggle={(x) => toggle('period', x)}
          />
        </Group>

        <Group label={t('filter.year')}>
          <ChipRow
            options={data.years.map((y) => ({
              value: String(y.value),
              label: <span className="tnum">{y.value}</span>,
              documents: y.documents,
              available: y.available,
            }))}
            selected={selection.year}
            onToggle={(x) => toggle('year', x)}
          />
        </Group>

        <Group label={t('filter.docType')} muted={data.docTypes.length === 1}>
          <ChipRow
            options={data.docTypes.map((d) => ({
              value: d.value,
              label: v('docType', d.value) ?? <Untranslated>{d.value}</Untranslated>,
              documents: d.documents,
              available: d.available,
            }))}
            selected={selection.docType}
            onToggle={(x) => toggle('docType', x)}
          />
        </Group>

        <Group label={t('filter.locale')} muted={data.locales.length === 1}>
          <ChipRow
            options={data.locales.map((l) => ({
              value: l.value,
              label: v('locale', l.value) ?? <Untranslated>{l.value}</Untranslated>,
              documents: l.documents,
              available: l.available,
            }))}
            selected={selection.locale}
            onToggle={(x) => toggle('locale', x)}
          />
        </Group>
      </div>

      {superadmin && <Group label={t('filter.quality')} hint={t('filter.quality.note')}>
        <ChipRow
          options={data.quality.map((q) => ({
            value: q.value,
            label:
              q.value === 'high' ? t('filter.quality.high')
              : q.value === 'medium' ? t('filter.quality.medium')
              : t('filter.quality.low'),
            documents: q.documents,
            available: q.available,
          }))}
          selected={selection.quality}
          onToggle={(x) => toggle('quality', x)}
        />
      </Group>}

      {/* --- footer --------------------------------------------------- */}
      {/* The count and the call to action sit side by side when there is room
          for both, and stack when there is not -- squeezing a two-clause
          count next to a full-width button gave three ragged lines on a
          phone. `pb-safe` because this is pinned to the bottom edge. */}
      <div className="pb-safe sticky bottom-0 -mx-3 mt-2 border-t border-[var(--border)] bg-[var(--bg)]/95 px-3 py-2.5 backdrop-blur-[2px] sm:-mx-4 sm:px-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            {nothing ? (
              <div>
                <div className="text-[length:var(--text-data)] text-[var(--fail)]">
                  {t('filter.empty')}
                </div>
                <div className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
                  {t('filter.emptyHint', { hint: emptyHint(data, selection, dimensionLabel) })}
                </div>
              </div>
            ) : (
              <div className="tnum text-[length:var(--text-data)] text-[var(--text-muted)]">
                <span className="font-medium text-[var(--text)]">
                  {tn('common.documents', matched.documents, { n: count(matched.documents, locale) })}
                </span>
                <Sep />
                {tn('common.companies', matched.companies, { n: count(matched.companies, locale) })}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {narrowed && (
              <Button variant="ghost" onClick={clear}>
                {t('filter.reset')}
              </Button>
            )}
            <Button className="flex-1 sm:flex-none" variant="primary" disabled={nothing} onClick={explore}>
              {narrowed
                ? t('filter.explore')
                : t('filter.exploreAll', {
                    documents: tn('common.documents', matched.documents, {
                      n: count(matched.documents, locale),
                    }),
                  })}
              <ArrowRight size={15} className="rtl:-scale-x-100" />
            </Button>
          </div>
        </div>
      </div>
    </Column>
  )
}

/**
 * The reason a selection matched nothing, named rather than left to be
 * guessed at.
 *
 * This is exact, not a heuristic. Facet counts are computed with their own
 * dimension dropped (§7.1), so a *selected* value whose count is zero means
 * "with every other filter applied, nothing has this value" -- which makes
 * that dimension the culprit. If several are, the one with the fewest live
 * options is the one to widen first.
 */
function emptyHint(
  data: FiltersResponse,
  selection: Selection,
  label: (d: Dimension) => string,
): string {
  const facets: Record<Dimension, { value: string; documents: number }[]> = {
    sector: data.sectors,
    industry: data.industries,
    symbol: data.companies.map((c) => ({ value: c.symbol, documents: c.documents })),
    docType: data.docTypes,
    period: data.periods,
    year: data.years.map((y) => ({ value: String(y.value), documents: y.documents })),
    locale: data.locales,
    quality: data.quality,
  }

  const culprits: { dimension: Dimension; liveOptions: number }[] = []
  for (const d of Object.keys(facets) as Dimension[]) {
    if (selection[d].length === 0) continue
    const list = facets[d]
    const anySelectedSurvives = selection[d].some(
      (v) => (list.find((o) => o.value === v)?.documents ?? 0) > 0,
    )
    if (!anySelectedSurvives) {
      culprits.push({ dimension: d, liveOptions: list.filter((o) => o.documents > 0).length })
    }
  }

  if (culprits.length === 0) return label('year')
  culprits.sort((a, b) => a.liveOptions - b.liveOptions)
  return culprits.map((c) => label(c.dimension)).join(', ')
}

const Sep = () => <span className="mx-1.5 text-[var(--border-strong)]">·</span>

function Column({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[46rem] px-4 pb-6 pt-5 sm:px-5">{children}</div>
  )
}

function ExploreSkeleton() {
  return (
    <Column>
      <Skeleton className="h-7 w-56" />
      <Skeleton className="mt-2 h-4 w-72" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="py-3">
          <Label className="mb-1.5">&nbsp;</Label>
          <Skeleton className="h-6 w-full" />
        </div>
      ))}
      <Skeleton className="h-64 w-full" />
    </Column>
  )
}
